export const ENTERPRISE_CASE_STUDY_OVERRIDES = {
  toshka: {
    name: "Toshka Lakefront",
    region: "New Toshka Lakes • New Valley",
    size: "200 Acres",
    solar: "20 kW",
    crops: "Barley, Alfalfa",
    coords: "23.80°N, 31.20°E",
    description:
      "A 200-acre coastal farm perched along the newly formed Toshka Lakes — built end-to-end from AI site discovery to full operation.",
    longDescription:
      "A 200-acre coastal farm perched along the newly formed Toshka Lakes — a natural Nile basin lake emerging in Egypt's New Valley. Farmtopia's AI-powered satellite analysis tools first identified this site as a high-potential opportunity, detecting favorable soil composition and microclimate conditions invisible to conventional scouting. The farm's high elevation (140–170 masl) and semi-humid microclimate proved ideal for near year-round cultivation. The project was delivered end-to-end to the investor: from raw desert to a legally registered, fully operational farm.",
    tagline: "Where AI discovered the opportunity, and engineering turned desert into a year-round farm.",
    mainChallenge:
      "Positioned uphill from a newly formed Nile basin lake, this 200-acre site demanded an irrigation system capable of pumping water upward at a 45° incline — powered entirely by solar energy. The soil carried elevated pH levels from decades of raw desert conditions, making it hostile to any crop without structured reclamation. And the farm sits more than five hours from the nearest supply chain for fertilizers, agrochemicals, or seed — meaning every operational decision had to be precise, planned months ahead, and managed remotely through software.",
    challengeBg: "linear-gradient(135deg, #d4c29d 0%, #a69165 40%, #1e293b 100%)",
    summaryTable: [
      { category: "Infrastructure", label: "Farm Area", value: "200 acres" },
      { category: "Infrastructure", label: "Solar Capacity", value: "20 kW" },
      { category: "Infrastructure", label: "Irrigation System", value: "14 km network, 600 m canal, 45° incline solar pump" },
      { category: "Infrastructure", label: "Elevation", value: "140–170 m ASL" },
      { category: "Production", label: "Crops", value: "Barley, Alfalfa" },
      { category: "Production", label: "Cumulative Yield", value: "270+ tons" },
      { category: "Production", label: "Operating Years", value: "4 (since Nov 2022)" },
      { category: "Efficiency", label: "Water Saved", value: "31%" },
      { category: "Efficiency", label: "Cost Trend", value: "↓ 12% YoY" },
      { category: "Efficiency", label: "Revenue Trend", value: "+18% YoY" },
    ],
    journey: [
      { year: "2021", phase: "Opportunity Discovery", detail: "Farmtopia's satellite analysis identified the Toshka lakefront as a high-potential site based on soil composition, microclimate, and topography invisible to conventional scouting." },
      { year: "2022", phase: "Engineering & Build", detail: "A 20 kW solar-powered pumping system, 600 m canal interface, and 14 km pressurized irrigation network were designed to handle 45° incline terrain and changing lake levels. The network was engineered to retain nutrient-rich biomaterial from the water source, channeling it directly into the soil as natural fertilizer. Legal and operational setup completed for investor handoff." },
      { year: "2023", phase: "Soil Restoration", detail: "Water and soil diagnostics followed by phased fertilization and restorative crop cycles to accelerate pH correction across the first 30 acres targeted for barley." },
      { year: "2024", phase: "First Barley Season", detail: "30 acres brought into barley production on restoration soil, yielding ~35 tons. Remote ERP and platform-managed agronomy addressed the 5+ hour supply chain constraint." },
      { year: "2025", phase: "Expansion & Diversification", detail: "40 new acres of alfalfa introduced alongside the second barley season. Cumulative yield surpassed 270 tons across 70 farmed acres of the 200-acre site." },
    ],
    metrics: [
      { label: "Area", value: "200 Acres" },
      { label: "Yield", value: "270+ tons" },
      { label: "Elevation", value: "140–170 masl" },
      { label: "Remote Supply Chain", value: "5+ hrs" },
    ],
    stats: {
      wells: 0,
      irrigationKm: 14,
      ndviGain: "+0.58",
      yieldTons: 270,
      waterSaved: "31%",
    },
    satelliteTimeseriesRequest: {
      area: [
        [31.2, 23.8],
        [31.205, 23.8],
        [31.205, 23.805],
        [31.2, 23.805],
      ],
      startDate: "2024-01-01",
      endDate: "2025-02-28",
      interval: "quarterly",
      maxScenes: 6,
      maxCloudCover: 70,
      layer: "NDVI",
      layers: ["NDVI", "NATURAL_COLOR", "MOISTURE"],
      label: "Toshka Lakefront",
    },
    publicPreviewId: "qLt08dSJRJMrZkZ5Jb71",
    technologyIndices: [0, 1, 2, 4],
    waterSource: "Toshka Lakes (pumped)",
    fieldsCount: 6,
    preview: {
      fields: [
        { id: "f1", title: "Field A – North Basin", area: "45 acres", currentCrop: "Barley", variety: "Giza 126", cropHealth: 78, seasonProgress: 82 },
        { id: "f2", title: "Field B – Central Plateau", area: "40 acres", currentCrop: "Alfalfa", variety: null, cropHealth: 84, seasonProgress: 65 },
        { id: "f3", title: "Field C – South Basin", area: "38 acres", currentCrop: "Barley", variety: "Giza 126", cropHealth: 72, seasonProgress: 80 },
        { id: "f4", title: "Field D – East Terrace", area: "35 acres", currentCrop: "Alfalfa", variety: null, cropHealth: 88, seasonProgress: 55 },
        { id: "f5", title: "Field E – West Strip", area: "22 acres", currentCrop: "Barley", variety: "Giza 130", cropHealth: 68, seasonProgress: 78 },
        { id: "f6", title: "Field F – Lake Edge", area: "20 acres", currentCrop: "Alfalfa", variety: null, cropHealth: 91, seasonProgress: 48 },
      ],
      ndviTimeseries: [
        {
          fieldId: "f1", fieldTitle: "Field A – North Basin", cropName: "Barley",
          data: [
            { date: "2022-03-01", ndvi: 0.08 }, { date: "2022-06-01", ndvi: 0.12 }, { date: "2022-09-01", ndvi: 0.18 },
            { date: "2022-12-01", ndvi: 0.22 }, { date: "2023-03-01", ndvi: 0.31 }, { date: "2023-06-01", ndvi: 0.38 },
            { date: "2023-09-01", ndvi: 0.42 }, { date: "2023-12-01", ndvi: 0.48 }, { date: "2024-03-01", ndvi: 0.54 },
            { date: "2024-06-01", ndvi: 0.58 }, { date: "2024-09-01", ndvi: 0.61 }, { date: "2024-12-01", ndvi: 0.56 },
            { date: "2025-03-01", ndvi: 0.63 }, { date: "2025-06-01", ndvi: 0.66 },
          ],
        },
        {
          fieldId: "f2", fieldTitle: "Field B – Central Plateau", cropName: "Alfalfa",
          data: [
            { date: "2022-03-01", ndvi: 0.06 }, { date: "2022-06-01", ndvi: 0.10 }, { date: "2022-09-01", ndvi: 0.15 },
            { date: "2022-12-01", ndvi: 0.20 }, { date: "2023-03-01", ndvi: 0.28 }, { date: "2023-06-01", ndvi: 0.35 },
            { date: "2023-09-01", ndvi: 0.44 }, { date: "2023-12-01", ndvi: 0.50 }, { date: "2024-03-01", ndvi: 0.56 },
            { date: "2024-06-01", ndvi: 0.62 }, { date: "2024-09-01", ndvi: 0.65 }, { date: "2024-12-01", ndvi: 0.60 },
            { date: "2025-03-01", ndvi: 0.68 }, { date: "2025-06-01", ndvi: 0.71 },
          ],
        },
      ],
      yieldHistory: [
        { season: "Season 1 (2022–23)", totalYield: 85, crops: [{ name: "Barley", yield: 55, area: "100 ac" }, { name: "Alfalfa", yield: 30, area: "60 ac" }] },
        { season: "Season 2 (2023–24)", totalYield: 210, crops: [{ name: "Barley", yield: 130, area: "120 ac" }, { name: "Alfalfa", yield: 80, area: "80 ac" }] },
        { season: "Season 3 (2024–25)", totalYield: 380, crops: [{ name: "Barley", yield: 225, area: "130 ac" }, { name: "Alfalfa", yield: 155, area: "70 ac" }] },
        { season: "Season 4 (2025–26)", totalYield: 540, crops: [{ name: "Barley", yield: 310, area: "130 ac" }, { name: "Alfalfa", yield: 230, area: "70 ac" }] },
      ],
      irrigation: {
        wue: 1.42,
        waterSaved: "31%",
        systemType: "Drip + Solar Pump",
        networkKm: 14,
        avgConsumption: "820",
        pumpSource: "45° Incline Solar Pump",
        monthlyUsage: [
          { month: "Jan", value: 620 }, { month: "Feb", value: 680 }, { month: "Mar", value: 850 },
          { month: "Apr", value: 940 }, { month: "May", value: 1100 }, { month: "Jun", value: 1250 },
          { month: "Jul", value: 1180 }, { month: "Aug", value: 1050 }, { month: "Sep", value: 880 },
          { month: "Oct", value: 750 }, { month: "Nov", value: 650 }, { month: "Dec", value: 600 },
        ],
      },
      finance: {
        costPerAcre: "EGP 18,500",
        revenuePerTon: "EGP 9,200",
        roi: "~34%",
        breakeven: "Year 3.5",
        costTrend: "↓ 12% YoY",
        revenueTrend: "+18% YoY",
      },
      ndviSnapshots: [
        { year: "2021", ndvi: "0.04", healthPct: null, note: "Bare desert" },
        { year: "2022", ndvi: "0.15", healthPct: "12", note: "First soil prep" },
        { year: "2023", ndvi: "0.38", healthPct: "45", note: "First crop cycle" },
        { year: "2024", ndvi: "0.58", healthPct: "72", note: "Full production" },
        { year: "2025", ndvi: "0.66", healthPct: "81", note: "Optimized cycles" },
      ],
      climate: {
        avgTemp: "32°C",
        humidity: "22%",
        annualRainfall: "3 mm",
        soilType: "Sandy (restored from raw desert)",
        windSpeed: "14 km/h",
        evapotranspiration: "8.2 mm/day",
        monthlyTemp: [
          { month: "Jan", high: 24, low: 8 }, { month: "Feb", high: 26, low: 10 }, { month: "Mar", high: 30, low: 14 },
          { month: "Apr", high: 35, low: 18 }, { month: "May", high: 39, low: 22 }, { month: "Jun", high: 42, low: 26 },
          { month: "Jul", high: 43, low: 27 }, { month: "Aug", high: 42, low: 26 }, { month: "Sep", high: 39, low: 23 },
          { month: "Oct", high: 34, low: 18 }, { month: "Nov", high: 28, low: 12 }, { month: "Dec", high: 24, low: 9 },
        ],
        challenges: ["Extreme heat (>42°C summers)", "Very low rainfall", "High evaporation rates"],
      },
      team: {
        totalMembers: 14,
        members: [
          { name: "Khaled S.", role: "Farm Manager", tasksCompleted: 218, status: "active" },
          { name: "Ahmed R.", role: "Irrigation Engineer", tasksCompleted: 176, status: "active" },
          { name: "Ibrahim H.", role: "Agronomist", tasksCompleted: 142, status: "active" },
          { name: "Youssef M.", role: "Field Supervisor", tasksCompleted: 195, status: "active" },
          { name: "Omar T.", role: "Equipment Operator", tasksCompleted: 108, status: "active" },
        ],
        tasksSummary: { total: 1240, completed: 1085, inProgress: 98, pending: 57 },
      },
      fertilization: {
        totalApplications: 36,
        currentCycle: "Spring 2025",
        methods: ["Drip fertigation", "Organic lake biomass"],
        history: [
          { date: "2025-01", type: "NPK 20-20-20", amount: "120 kg/ac", method: "Drip fertigation" },
          { date: "2024-10", type: "Organic compost", amount: "2 tons/ac", method: "Broadcast" },
          { date: "2024-07", type: "Urea 46%", amount: "80 kg/ac", method: "Drip fertigation" },
          { date: "2024-04", type: "DAP 18-46-0", amount: "100 kg/ac", method: "Drip fertigation" },
          { date: "2024-01", type: "Lake biomass", amount: "3 tons/ac", method: "Direct canal application" },
        ],
        annualTotals: { nitrogen: "145 kg/ac", phosphorus: "92 kg/ac", potassium: "68 kg/ac" },
        cyclesCompleted: 8,
      },
    },
  },
  farafra: {
    name: "Farafra Research Station",
    region: "El-Farafra, El-Kafah • New Valley",
    size: "150 Acres",
    solar: "100 kW Solar",
    crops: "Wheat, Barley, Alfalfa",
    coords: "27.056°N, 27.970°E",
    tagline: "Pushing the limits of irrigation efficiency in Egypt's harshest climate — and proving the science of Climate-Adaptive Irrigation works.",
    description:
      "A 150-acre proving ground for Farmtopia’s platform, stretching a single 380m well and 100 kW solar across 50% more land than conventional capacity.",
    longDescription:
      "Located in El-Farafra, El-Kafah Center in the New Valley Governorate, this 150-acre project was developed in collaboration with the Egyptian government to reclaim desert land for strategic crop production — starting with wheat to address national food security demand. It is also one of Farmtopia’s earliest privately owned assets and serves as the primary proving ground for the platform’s development.",
    mainChallenge:
      "Farafra tested the limits of resource efficiency from day one. The site faced hotter and drier conditions than Toshka, with higher evaporation rates, more alkaline soil, and no Nile-fed natural buffering to ease soil recovery. The engineering challenge was equally sharp: make a 380-meter well and a 100 kW solar station support irrigation that will eventually serve all 150 acres. A footprint larger than what similar configurations are typically expected to carry. The question was not whether the land could be irrigated, but whether it could be made economically and agronomically viable at scale.",
    challengeBg: "linear-gradient(135deg, #f59e0b 0%, #b45309 30%, #1e293b 100%)",
    summaryTable: [
      { category: "Infrastructure", label: "Farm Area", value: "150 acres" },
      { category: "Infrastructure", label: "Solar Capacity", value: "100 kW" },
      { category: "Infrastructure", label: "Well Depth", value: "380 m" },
      { category: "Infrastructure", label: "Wells", value: "1" },
      { category: "Infrastructure", label: "Irrigation Network", value: "11 km" },
      { category: "Infrastructure", label: "Irrigation Type", value: "Flood, Drip (Climate-Adaptive Schedules)" },
      { category: "Infrastructure", label: "Capacity Stretch", value: "+50% beyond rated hardware" },
      { category: "Production", label: "Crops", value: "Wheat, Barley, Alfalfa" },
      { category: "Production", label: "Cumulative Yield", value: "250+ tons" },
      { category: "Production", label: "Operating Years", value: "3 (since Apr 2023)" },
      { category: "Production", label: "Restoration Method", value: "Acid washing, water washing, arugula cycle" },
      { category: "Efficiency", label: "Water Use Efficiency", value: "1.65x baseline" },
    ],
    journey: [
      { year: "2022", phase: "Government Collaboration", detail: "Desert reclamation project initiated with the Egyptian government for strategic wheat production in Farafra. Site developed as a Farmtopia-owned proving ground." },
      { year: "2023", phase: "First 15 Acres", detail: "Acid-based pH remediation, water washing cycles, and arugula rotation deployed on the first 15 acres. Soil and water diagnostics established baseline conditions. First restorative crop cycle yielded ~15 tons." },
      { year: "2024", phase: "Expansion to 50 Acres", detail: "Climate-adaptive irrigation engine stretched effective coverage 50% beyond hardware capacity. Farmed area tripled to 50 acres. First wheat and barley harvests produced ~75 tons." },
      { year: "2025", phase: "80 Acres in Production", detail: "Farmed footprint reached 80 acres with ~160 tons produced — bringing cumulative yield past 250 tons. Platform-managed fertilization and irrigation driving continued expansion toward the full 150-acre capacity." },
    ],
    metrics: [
      { label: "Area", value: "150 Acres" },
      { label: "Solar", value: "100 kW" },
      { label: "Well Depth", value: "380 m" },
      { label: "Capacity Stretch", value: "+50%" },
      { label: "Yield", value: "250+ tons" },
    ],
    stats: {
      wells: 1,
      irrigationKm: 11,
      ndviGain: "+0.44",
      yieldTons: 250,
      waterSaved: "36%",
      wue: "1.65x",
    },
    satelliteTimeseriesRequest: {
      area: [
        [27.97, 27.056],
        [27.975, 27.056],
        [27.975, 27.061],
        [27.97, 27.061],
      ],
      startDate: "2024-01-01",
      endDate: "2025-02-28",
      interval: "quarterly",
      maxScenes: 6,
      maxCloudCover: 70,
      layer: "NDVI",
      layers: ["NDVI", "NATURAL_COLOR", "MOISTURE"],
      label: "Farafra Research Station",
    },
    publicPreviewId: "xT4POIntwQoIKVElnpF0",
    technologyIndices: [1, 4, 5, 6],
    waterSource: "380 m deep well",
    fieldsCount: 4,
    preview: {
      fields: [
        { id: "f1", title: "Field A – West Block", area: "45 acres", currentCrop: "Wheat", variety: "Misr 3", cropHealth: 82, seasonProgress: 75 },
        { id: "f2", title: "Field B – East Block", area: "40 acres", currentCrop: "Barley", variety: "Giza 126", cropHealth: 76, seasonProgress: 70 },
        { id: "f3", title: "Field C – South Block", area: "35 acres", currentCrop: "Wheat", variety: "Misr 3", cropHealth: 79, seasonProgress: 68 },
        { id: "f4", title: "Field D – Research Plot", area: "30 acres", currentCrop: "Trial Crops", variety: null, cropHealth: 65, seasonProgress: 55 },
      ],
      ndviTimeseries: [
        {
          fieldId: "f1", fieldTitle: "Field A – West Block", cropName: "Wheat",
          data: [
            { date: "2023-01-01", ndvi: 0.10 }, { date: "2023-04-01", ndvi: 0.18 }, { date: "2023-07-01", ndvi: 0.25 },
            { date: "2023-10-01", ndvi: 0.32 }, { date: "2024-01-01", ndvi: 0.40 }, { date: "2024-04-01", ndvi: 0.46 },
            { date: "2024-07-01", ndvi: 0.50 }, { date: "2024-10-01", ndvi: 0.48 }, { date: "2025-01-01", ndvi: 0.52 },
            { date: "2025-04-01", ndvi: 0.55 },
          ],
        },
        {
          fieldId: "f2", fieldTitle: "Field B – East Block", cropName: "Barley",
          data: [
            { date: "2023-01-01", ndvi: 0.08 }, { date: "2023-04-01", ndvi: 0.14 }, { date: "2023-07-01", ndvi: 0.22 },
            { date: "2023-10-01", ndvi: 0.28 }, { date: "2024-01-01", ndvi: 0.36 }, { date: "2024-04-01", ndvi: 0.42 },
            { date: "2024-07-01", ndvi: 0.44 }, { date: "2024-10-01", ndvi: 0.42 }, { date: "2025-01-01", ndvi: 0.46 },
            { date: "2025-04-01", ndvi: 0.50 },
          ],
        },
      ],
      yieldHistory: [
        { season: "Season 1 (2023–24)", totalYield: 120, crops: [{ name: "Wheat", yield: 75, area: "80 ac" }, { name: "Barley", yield: 45, area: "50 ac" }] },
        { season: "Season 2 (2024–25)", totalYield: 260, crops: [{ name: "Wheat", yield: 160, area: "85 ac" }, { name: "Barley", yield: 100, area: "55 ac" }] },
        { season: "Season 3 (2025–26)", totalYield: 410, crops: [{ name: "Wheat", yield: 250, area: "90 ac" }, { name: "Barley", yield: 160, area: "60 ac" }] },
      ],
      irrigation: {
        wue: 1.50,
        waterSaved: "36%",
        systemType: "Drip (Climate-Adaptive)",
        networkKm: 11,
        avgConsumption: "680",
        pumpSource: "100 kW Solar",
        monthlyUsage: [
          { month: "Jan", value: 480 }, { month: "Feb", value: 520 }, { month: "Mar", value: 640 },
          { month: "Apr", value: 780 }, { month: "May", value: 920 }, { month: "Jun", value: 1050 },
          { month: "Jul", value: 980 }, { month: "Aug", value: 870 }, { month: "Sep", value: 720 },
          { month: "Oct", value: 600 }, { month: "Nov", value: 510 }, { month: "Dec", value: 460 },
        ],
      },
      finance: {
        costPerAcre: "EGP 16,200",
        revenuePerTon: "EGP 8,800",
        roi: "~28%",
        breakeven: "Year 3",
        costTrend: "↓ 8% YoY",
        revenueTrend: "+15% YoY",
      },
      ndviSnapshots: [
        { year: "2023", ndvi: "0.10", healthPct: "8", note: "Desert baseline" },
        { year: "2024", ndvi: "0.38", healthPct: "42", note: "First wheat harvest" },
        { year: "2025", ndvi: "0.52", healthPct: "68", note: "Optimized irrigation" },
      ],
      climate: {
        avgTemp: "36°C",
        humidity: "18%",
        annualRainfall: "1 mm",
        soilType: "Alkaline sandy (pH-corrected)",
        windSpeed: "16 km/h",
        evapotranspiration: "9.5 mm/day",
        monthlyTemp: [
          { month: "Jan", high: 22, low: 6 }, { month: "Feb", high: 25, low: 8 }, { month: "Mar", high: 30, low: 12 },
          { month: "Apr", high: 36, low: 16 }, { month: "May", high: 41, low: 21 }, { month: "Jun", high: 44, low: 25 },
          { month: "Jul", high: 45, low: 27 }, { month: "Aug", high: 44, low: 26 }, { month: "Sep", high: 41, low: 22 },
          { month: "Oct", high: 35, low: 17 }, { month: "Nov", high: 27, low: 11 }, { month: "Dec", high: 22, low: 7 },
        ],
        challenges: ["Highest temps in Egypt (>44°C)", "Near-zero rainfall", "Alkaline soil requiring continuous correction"],
      },
      team: {
        totalMembers: 10,
        members: [
          { name: "Hassan A.", role: "Station Director", tasksCompleted: 312, status: "active" },
          { name: "Mahmoud K.", role: "Irrigation Specialist", tasksCompleted: 245, status: "active" },
          { name: "Fatma N.", role: "Soil Scientist", tasksCompleted: 198, status: "active" },
          { name: "Ali B.", role: "Field Technician", tasksCompleted: 167, status: "active" },
        ],
        tasksSummary: { total: 980, completed: 856, inProgress: 74, pending: 50 },
      },
      fertilization: {
        totalApplications: 28,
        currentCycle: "Winter 2025",
        methods: ["Drip fertigation", "pH correction amendments"],
        history: [
          { date: "2025-02", type: "NPK 19-19-19", amount: "100 kg/ac", method: "Drip fertigation" },
          { date: "2024-11", type: "Sulfuric acid wash", amount: "30 L/ac", method: "Soil injection" },
          { date: "2024-08", type: "Potassium sulfate", amount: "60 kg/ac", method: "Drip fertigation" },
          { date: "2024-05", type: "Ammonium nitrate", amount: "90 kg/ac", method: "Drip fertigation" },
        ],
        annualTotals: { nitrogen: "130 kg/ac", phosphorus: "78 kg/ac", potassium: "55 kg/ac" },
        cyclesCompleted: 6,
      },
    },
  },
  fayoum: {
    name: "Fayoum Regenerative Venture",
    region: "Fayoum Governorate",
    size: "50 Acres",
    solar: "Integrated energy systems",
    crops: "Fodder, Wheat, Livestock",
    coords: "29.308°N, 30.842°E",
    tagline: "From planet to plate — where crops feed livestock, livestock feeds the soil, and software connects the whole system to feed you with negative carbon footprint.",
    description:
      "A fully integrated, regenerative agricultural system connecting crop production, livestock management, and digital technology into a single, self-reinforcing value chain.",
    longDescription:
      "Located near Farmtopia's Founder's hometown in Fayoum Governorate, this ongoing venture represents the culmination of Farmtopia's founding vision: a fully integrated, regenerative agricultural system with negative carbon footprint that connects crop production, livestock management, and digital technology into a single, self-reinforcing value chain. The 50-acre farm produces forage crops and wheat, feeding directly into an on-site operation of 50 cattle and 120 sheep. The livestock, in turn, produce organic fertilizer that returns to the fields — dramatically reducing input costs and eliminating dependency on external supply chains. Beyond farming, Fayoum serves as Farmtopia's active R&D laboratory, expanding the platform into herd health monitoring, dairy production metrics, fattening cycle tracking, and full digital traceability — from GPS-tagged field coordinates to individual animal health records.",
    mainChallenge:
      "Fayoum is not primarily a land-restoration story. It is an integration story. The challenge is to make crop production, feed planning, cattle, sheep, nutrient cycling, and day-to-day operations work as one coordinated system rather than as separate farm activities. That means balancing forage and wheat output against herd demand, returning livestock-generated organic fertilizer back to the soil efficiently, and expanding the platform beyond field operations into herd health, dairy and fattening metrics, and traceability.",
    challengeBg: "linear-gradient(135deg, #166534 0%, #14532d 30%, #1e293b 100%)",
    summaryTable: [
      { category: "Infrastructure", label: "Farm Area", value: "50 acres" },
      { category: "Livestock", label: "Cattle", value: "50 cows" },
      { category: "Livestock", label: "Sheep", value: "120 sheep" },
      { category: "Livestock", label: "System Model", value: "Regenerative Circular" },
      { category: "Production", label: "Annual Yield", value: "~250 tons/year" },
      { category: "Production", label: "Fodder (Berseem)", value: "~200 tons/yr / 25 ac (8 cuts)" },
      { category: "Production", label: "Wheat (Winter)", value: "~45 tons/yr / 15 ac" },
      { category: "Production", label: "Rotational Pasture", value: "10 ac (Grazing)" },
      { category: "Production", label: "Operating Years", value: "3 (since Dec 2022)" },
    ],
    journey: [
      { year: "2022", phase: "Venture Launch", detail: "50-acre farm established near Farmtopia's home base in Fayoum. Designed from day one as an integrated crop-livestock operation with regenerative principles." },
      { year: "2023", phase: "Livestock Integration", detail: "50 cattle and 120 sheep brought on-site. Organic fertilizer loop activated — livestock waste returned to fields, reducing external input costs." },
      { year: "2024", phase: "Circular Model Active", detail: "Forage and wheat production feeding directly into livestock operations. Platform expanding into herd health monitoring and feed planning." },
      { year: "2025", phase: "R&D Expansion", detail: "Platform being extended into dairy production metrics, fattening cycle tracking, nutrient cycling analytics, and full digital traceability from field to animal." },
    ],
    metrics: [
      { label: "Area", value: "50 Acres" },
      { label: "Cows", value: "50" },
      { label: "Sheep", value: "120" },
      { label: "Model", value: "Regenerative" },
      { label: "Annual Yield", value: "250+ tons/yr" },
    ],
    stats: {
      wells: 1,
      irrigationKm: 6,
      ndviGain: "+0.29",
      yieldTons: 180,
      waterSaved: "24%",
      wue: "1.18x",
    },
    satelliteTimeseriesRequest: {
      area: [
        [30.842, 29.308],
        [30.847, 29.308],
        [30.847, 29.313],
        [30.842, 29.313],
      ],
      startDate: "2024-01-01",
      endDate: "2025-02-28",
      interval: "quarterly",
      maxScenes: 6,
      maxCloudCover: 70,
      layer: "NDVI",
      layers: ["NDVI", "NATURAL_COLOR", "MOISTURE"],
      label: "Fayoum Regenerative Venture",
    },
    publicPreviewId: "yOisrgKgvQt6pxyRDONy",
    technologyIndices: [0, 6, 7, 2],
    waterSource: "Canal + groundwater",
    fieldsCount: 3,
    preview: {
      fields: [
        { id: "f1", title: "Fodder Block", area: "25 acres", currentCrop: "Berseem Clover", variety: null, cropHealth: 85, seasonProgress: 60 },
        { id: "f2", title: "Wheat Block", area: "15 acres", currentCrop: "Wheat", variety: "Misr 3", cropHealth: 78, seasonProgress: 72 },
        { id: "f3", title: "Mixed Pasture", area: "10 acres", currentCrop: "Rotational Grazing", variety: null, cropHealth: 70, seasonProgress: 45 },
      ],
      ndviTimeseries: [
        {
          fieldId: "f1", fieldTitle: "Fodder Block", cropName: "Berseem Clover",
          data: [
            { date: "2024-01-01", ndvi: 0.22 }, { date: "2024-03-01", ndvi: 0.30 }, { date: "2024-05-01", ndvi: 0.38 },
            { date: "2024-07-01", ndvi: 0.32 }, { date: "2024-09-01", ndvi: 0.36 }, { date: "2024-11-01", ndvi: 0.42 },
            { date: "2025-01-01", ndvi: 0.48 }, { date: "2025-03-01", ndvi: 0.52 },
          ],
        },
        {
          fieldId: "f2", fieldTitle: "Wheat Block", cropName: "Wheat",
          data: [
            { date: "2024-01-01", ndvi: 0.18 }, { date: "2024-03-01", ndvi: 0.25 }, { date: "2024-05-01", ndvi: 0.35 },
            { date: "2024-07-01", ndvi: 0.28 }, { date: "2024-09-01", ndvi: 0.32 }, { date: "2024-11-01", ndvi: 0.38 },
            { date: "2025-01-01", ndvi: 0.44 }, { date: "2025-03-01", ndvi: 0.48 },
          ],
        },
      ],
      yieldHistory: [
        { season: "Season 1 (2024–25)", totalYield: 110, crops: [{ name: "Fodder", yield: 65, area: "25 ac" }, { name: "Wheat", yield: 45, area: "15 ac" }] },
        { season: "Season 2 (2025–26)", totalYield: 180, crops: [{ name: "Fodder", yield: 105, area: "25 ac" }, { name: "Wheat", yield: 75, area: "15 ac" }] },
      ],
      irrigation: {
        wue: 1.18,
        waterSaved: "24%",
        systemType: "Mixed (Drip + Flood)",
        networkKm: 6,
        avgConsumption: "950",
        pumpSource: "Grid + Solar Hybrid",
        monthlyUsage: [
          { month: "Jan", value: 700 }, { month: "Feb", value: 750 }, { month: "Mar", value: 900 },
          { month: "Apr", value: 1050 }, { month: "May", value: 1200 }, { month: "Jun", value: 1350 },
          { month: "Jul", value: 1280 }, { month: "Aug", value: 1100 }, { month: "Sep", value: 920 },
          { month: "Oct", value: 780 }, { month: "Nov", value: 700 }, { month: "Dec", value: 680 },
        ],
      },
      finance: {
        costPerAcre: "EGP 22,000",
        revenuePerTon: "EGP 7,500",
        roi: "~18%",
        breakeven: "Year 4",
        costTrend: "↓ 5% YoY",
        revenueTrend: "+12% YoY",
      },
      ndviSnapshots: [
        { year: "2024", ndvi: "0.20", healthPct: "18", note: "Initial planting" },
        { year: "2025", ndvi: "0.48", healthPct: "58", note: "Circular model active" },
      ],
      climate: {
        avgTemp: "26°C",
        humidity: "52%",
        annualRainfall: "18 mm",
        soilType: "Alluvial clay (Nile valley)",
        windSpeed: "8 km/h",
        evapotranspiration: "5.8 mm/day",
        monthlyTemp: [
          { month: "Jan", high: 20, low: 7 }, { month: "Feb", high: 22, low: 8 }, { month: "Mar", high: 26, low: 11 },
          { month: "Apr", high: 30, low: 15 }, { month: "May", high: 34, low: 19 }, { month: "Jun", high: 37, low: 22 },
          { month: "Jul", high: 38, low: 23 }, { month: "Aug", high: 37, low: 23 }, { month: "Sep", high: 35, low: 21 },
          { month: "Oct", high: 31, low: 17 }, { month: "Nov", high: 25, low: 12 }, { month: "Dec", high: 21, low: 8 },
        ],
        challenges: ["Moderate heat stress in summer", "High humidity near canals", "Nutrient-heavy livestock runoff management"],
      },
      team: {
        totalMembers: 8,
        members: [
          { name: "Amr K.", role: "Operations Lead", tasksCompleted: 284, status: "active" },
          { name: "Reda M.", role: "Livestock Manager", tasksCompleted: 210, status: "active" },
          { name: "Samir E.", role: "Agronomist", tasksCompleted: 165, status: "active" },
          { name: "Nour H.", role: "Veterinarian", tasksCompleted: 132, status: "active" },
        ],
        tasksSummary: { total: 860, completed: 742, inProgress: 68, pending: 50 },
      },
      fertilization: {
        totalApplications: 18,
        currentCycle: "Spring 2025",
        methods: ["Organic compost (livestock)", "Drip fertigation", "Green manure rotation"],
        history: [
          { date: "2025-03", type: "Cattle manure compost", amount: "4 tons/ac", method: "Broadcast + incorporation" },
          { date: "2025-01", type: "NPK 15-15-15", amount: "80 kg/ac", method: "Drip fertigation" },
          { date: "2024-10", type: "Green manure (berseem residue)", amount: "3 tons/ac", method: "Plowing under" },
          { date: "2024-07", type: "Sheep manure compost", amount: "2.5 tons/ac", method: "Side dressing" },
        ],
        annualTotals: { nitrogen: "110 kg/ac", phosphorus: "65 kg/ac", potassium: "50 kg/ac" },
        cyclesCompleted: 5,
      },
    },
  },
  cotton: {
    name: "Cotton Varietal Trials",
    region: "Dry Climate Trials • Western Desert",
    size: "Research plots",
    solar: "Drip irrigation program",
    crops: "Egyptian Cotton: Giza 94, 95, 97, 98",
    coords: "27.056°N, 27.970°E",
    tagline: "Peer-reviewed proof that precision agriculture software measurably increases yield — even for Egypt’s most demanding cash crop.",
    description:
      "Applied research in partnership with Egypt’s Cotton Research Institute proving that platform-guided operations measurably improve yield and reduce water use in restored desert land.",
    longDescription:
      "Cotton remains one of Egypt's most promising and historically significant cash crops. But growing it in newly reclaimed desert land — under drip irrigation in a dry climate — has remained an open challenge. Farmtopia partnered with Egypt's Cotton Research Institute (under the Agricultural Research Center) to address it head-on.",
    mainChallenge:
      "Cotton is one of Egypt's highest-value crops, but newly restored desert land is one of the hardest environments in which to establish it. Under drip irrigation and dry-climate conditions, variety selection, heat stress response, irrigation timing, and fertilization precision all become decisive. This project had a dual challenge: identify the best-performing variety under these conditions, and measure whether platform-guided farm management delivers a real operational advantage over conventional practice — with results rigorous enough for peer-reviewed publication.",
    challengeBg: "linear-gradient(135deg, #92400e 0%, #78350f 30%, #1e293b 100%)",
    storyNarrative: [
      "Cotton remains one of Egypt's most important and promising cash crops, but growing it successfully in newly restored desert land under dry conditions and drip irrigation systems remains a significant agronomic challenge. To address this, Farmtopia partnered with Egypt's Cotton Research Institute, under the Agricultural Research Center, to evaluate cotton performance under reclaimed desert conditions and test the role of software-guided management in improving outcomes.",
      "The trials were conducted on second-cycle restored desert land in the Farafra region. Several Egyptian cotton varieties were tested, including Giza 94, Giza 95, Giza 97, and the newly developed Giza 98 — a high-staple-length variety specifically bred for adaptation to dry climates and drip irrigation systems.",
      "The findings were clear. Giza 98 showed the strongest performance under dry-climate desert conditions, validating its suitability for cultivation in newly restored land. At the same time, fields managed with Farmtopia's platform features demonstrated measurable gains in both productivity and irrigation efficiency compared to conventionally managed plots.",
      "The research has been published and peer-reviewed.",
    ],
    summaryTable: [
      { category: "Research", label: "Research Partner", value: "Cotton Research Institute, ARC" },
      { category: "Research", label: "Study Type", value: "Peer-reviewed Applied R&D" },
      { category: "Research", label: "Trial Design", value: "Platform-managed vs conventional plots" },
      { category: "Research", label: "Varieties Tested", value: "Giza 94, 95, 97, 98" },
      { category: "Research", label: "Lead Variety", value: "Giza 98" },
      { category: "Production", label: "Total Yield", value: "10 tons" },
      { category: "Production", label: "Giza 98 Yield", value: "32 qintar / 2 ac" },
      { category: "Production", label: "Giza 94 Yield", value: "12 qintar / 2 ac" },
      { category: "Production", label: "Giza 95 Yield", value: "12 qintar / 2 ac" },
      { category: "Production", label: "Giza 97 Yield", value: "12 qintar / 2 ac" },
      { category: "Efficiency", label: "Water Use Efficiency", value: "+22% (platform vs conventional)" },
      { category: "Efficiency", label: "Heat-wave Yield Advantage", value: "Significant increase" },
      { category: "Efficiency", label: "NDVI Gain", value: "+0.22" },
      { category: "Efficiency", label: "Irrigation Type", value: "Precision Drip" },
    ],
    journey: [
      { year: "2023", phase: "Research Design", detail: "Partnership formed with Cotton Research Institute. Trial methodology established — platform-managed plots vs conventional comparison. Varieties Giza 94, 95, 97, 98 allocated to field plots." },
      { year: "2024", phase: "Trial Season", detail: "All varieties planted on second-cycle restored desert land in Farafra region. Platform-guided irrigation, crop-stage tracking, and fertilization managed across treatment plots. Heat-wave events tested climate-stress response." },
      { year: "2025", phase: "Publication", detail: "Giza 98 confirmed as strongest performer under dry-climate conditions. Platform-managed fields showed measurable yield and water-efficiency gains. Results published, peer-reviewed, and independently validated across multiple ARC trial sites in Egypt." },
    ],
    metrics: [
      { label: "Crop Focus", value: "Gossypium barbadense (Egyptian Cotton)" },
      { label: "Lead Variety", value: "Giza 98" },
      { label: "Water Use Efficiency", value: "+22%" },
      { label: "Heat-wave Adaptation", value: "Validated" },
      { label: "Study Type", value: "Applied R&D" },
    ],
    stats: {
      wells: 1,
      irrigationKm: 4,
      ndviGain: "+0.22",
      yieldTons: 10,
      waterSaved: "22%",
      wue: "1.22x",
    },
    satelliteTimeseriesRequest: {
      area: [
        [27.97, 27.056],
        [27.975, 27.056],
        [27.975, 27.061],
        [27.97, 27.061],
      ],
      startDate: "2024-01-01",
      endDate: "2025-02-28",
      interval: "quarterly",
      maxScenes: 6,
      maxCloudCover: 70,
      layer: "NDVI",
      layers: ["NDVI", "NATURAL_COLOR", "MOISTURE"],
      label: "Cotton Varietal Trials",
    },
    publicPreviewId: "1QzCqNpXJEXetChGrMw4",
    technologyIndices: [1, 2, 4, 7],
    waterSource: "Deep well (shared)",
    fieldsCount: 5,
    preview: {
      fields: [
        { id: "f1", title: "Plot A – Giza 98", area: "2 acres", currentCrop: "Cotton", variety: "Giza 98", cropHealth: 82, seasonProgress: 88 },
        { id: "f2", title: "Plot B \u2013 Giza 94", area: "2 acres", currentCrop: "Cotton", variety: "Giza 94", cropHealth: 64, seasonProgress: 84 },
        { id: "f3", title: "Plot C \u2013 Giza 95", area: "2 acres", currentCrop: "Cotton", variety: "Giza 95", cropHealth: 68, seasonProgress: 85 },
        { id: "f4", title: "Plot D \u2013 Giza 97", area: "2 acres", currentCrop: "Cotton", variety: "Giza 97", cropHealth: 72, seasonProgress: 86 },
      ],
      ndviTimeseries: [
        {
          fieldId: "f1", fieldTitle: "Giza 98 (Lead)", cropName: "Cotton",
          data: [
            { date: "2024-04-01", ndvi: 0.12 }, { date: "2024-05-15", ndvi: 0.22 }, { date: "2024-06-15", ndvi: 0.35 },
            { date: "2024-07-15", ndvi: 0.45 }, { date: "2024-08-15", ndvi: 0.52 }, { date: "2024-09-15", ndvi: 0.48 },
            { date: "2024-10-15", ndvi: 0.40 },
          ],
        },
        {
          fieldId: "f2", fieldTitle: "Giza 94 (Control)", cropName: "Cotton",
          data: [
            { date: "2024-04-01", ndvi: 0.10 }, { date: "2024-05-15", ndvi: 0.18 }, { date: "2024-06-15", ndvi: 0.28 },
            { date: "2024-07-15", ndvi: 0.34 }, { date: "2024-08-15", ndvi: 0.38 }, { date: "2024-09-15", ndvi: 0.35 },
            { date: "2024-10-15", ndvi: 0.30 },
          ],
        },
      ],
      yieldHistory: [
        { season: "Trial Season (2024)", totalYield: 10, unit: "tons", crops: [
          { name: "Giza 98", yield: 32, unit: "qintar", area: "2 ac" },
          { name: "Giza 94", yield: 12, unit: "qintar", area: "2 ac" },
          { name: "Giza 95", yield: 12, unit: "qintar", area: "2 ac" },
          { name: "Giza 97", yield: 12, unit: "qintar", area: "2 ac" },
        ]},
      ],
      irrigation: {
        wue: 1.22,
        waterSaved: "22%",
        systemType: "Precision Drip",
        networkKm: 4,
        avgConsumption: "420",
        pumpSource: "Solar",
        monthlyUsage: [
          { month: "Apr", value: 280 }, { month: "May", value: 380 }, { month: "Jun", value: 520 },
          { month: "Jul", value: 580 }, { month: "Aug", value: 540 }, { month: "Sep", value: 420 },
          { month: "Oct", value: 300 },
        ],
      },
      finance: {
        costPerAcre: "EGP 14,000",
        revenuePerTon: "EGP 42,000",
        roi: "R&D (non-commercial)",
        breakeven: "N/A (Research)",
      },
      ndviSnapshots: [
        { year: "2024", ndvi: "0.12", healthPct: "10", note: "Trial start" },
        { year: "2025", ndvi: "0.52", healthPct: "72", note: "Giza 98 peak" },
      ],
      climate: {
        avgTemp: "36°C",
        humidity: "18%",
        annualRainfall: "1 mm",
        soilType: "Restored sandy (2nd cycle reclamation)",
        windSpeed: "15 km/h",
        evapotranspiration: "9.2 mm/day",
        monthlyTemp: [
          { month: "Apr", high: 36, low: 16 }, { month: "May", high: 41, low: 21 },
          { month: "Jun", high: 44, low: 25 }, { month: "Jul", high: 45, low: 27 },
          { month: "Aug", high: 44, low: 26 }, { month: "Sep", high: 41, low: 22 },
          { month: "Oct", high: 35, low: 17 },
        ],
        challenges: ["Extreme heat during boll formation", "Low humidity stress", "Second-cycle soil variability"],
      },
      team: {
        totalMembers: 6,
        members: [
          { name: "Dr. Laila F.", role: "Lead Researcher (CRI)", tasksCompleted: 95, status: "active" },
          { name: "Mohamed S.", role: "Trial Coordinator", tasksCompleted: 128, status: "active" },
          { name: "Tarek W.", role: "Field Technician", tasksCompleted: 84, status: "active" },
        ],
        tasksSummary: { total: 340, completed: 298, inProgress: 25, pending: 17 },
      },
      fertilization: {
        totalApplications: 14,
        currentCycle: "Trial Season 2024",
        methods: ["Precision drip fertigation", "Foliar micronutrients"],
        history: [
          { date: "2024-09", type: "Potassium boost (boll stage)", amount: "50 kg/ac", method: "Drip fertigation" },
          { date: "2024-07", type: "Calcium nitrate", amount: "40 kg/ac", method: "Drip fertigation" },
          { date: "2024-06", type: "Foliar Zn + B", amount: "2 L/ac", method: "Foliar spray" },
          { date: "2024-05", type: "NPK 12-12-17", amount: "100 kg/ac", method: "Drip fertigation" },
        ],
        annualTotals: { nitrogen: "95 kg/ac", phosphorus: "52 kg/ac", potassium: "72 kg/ac" },
        cyclesCompleted: 1,
      },
    },
  },
  minya: {
    name: "Minya Partner Farm",
    region: "Minya Governorate",
    size: "150 Acres",
    solar: "200 kW",
    crops: "Various",
    coords: "28.086°N, 30.761°E",
    lightCase: true,
    tagline: "The first farm where Farmtopia's development model was applied as a service \u2014 proving the system works beyond the company's own operational comfort.",
    description:
      "An external client deployment demonstrating Farmtopia’s ADH-as-a-Service model — applying the same discipline used in internally developed farms to third-party operations.",
    longDescription:
      "The Minya project marks Farmtopia's first engagement with an external commercial farm, applying the same agricultural development model, platform intelligence, and operational discipline used across Farmtopia's own projects. Beyond software and advisory services, Farmtopia delivered full infrastructure \u2014 including a 200 kW solar station installation and well drilling \u2014 alongside a complete management layer covering agronomic planning, irrigation logic, crop scheduling, and daily operations as a hands-on service partnership.",
    mainChallenge:
      "Building and operating your own farms is one thing. Delivering the same results for someone else \u2014 on their land, with their teams, and under their commercial constraints \u2014 is a fundamentally different challenge. For Minya, Farmtopia went beyond advisory \u2014 designing and installing the 200 kW solar energy system, drilling the water wells, and deploying the irrigation infrastructure before applying the same structured approach to agronomy, crop planning, and platform-backed decision-making that powers its own farms.",
    challengeBg: "linear-gradient(135deg, #475569 0%, #1e293b 100%)",
    storyNarrative: [
      "The Minya project marks Farmtopia's first engagement with an external commercial farm, applying the same agricultural development model, platform intelligence, and operational discipline used across Farmtopia's own projects.",
      "Beyond software and advisory services, Farmtopia delivered full infrastructure \u2014 including a 200 kW solar station installation and well drilling \u2014 alongside a complete management layer covering agronomic planning, irrigation logic, crop scheduling, and daily operations as a hands-on service partnership.",
      "This marks an important expansion of Farmtopia's offering: not only building and operating farms directly, but also extending its methods, intelligence, and management systems to outside agricultural clients.",
    ],
    summaryTable: [
      { label: "Farm Area", value: "150 acres" },
      { label: "Solar Capacity", value: "200 kW (installed by Farmtopia)" },
      { label: "Well Drilling", value: "Delivered by Farmtopia" },
      { label: "Service Model", value: "Agricultural Development House (as a Service)" },
      { label: "Partnership Type", value: "External Commercial Farm" },
      { label: "Region", value: "Minya Governorate" },
      { label: "Services Delivered", value: "Solar installation, well drilling, irrigation infrastructure, agronomic planning, crop scheduling, operations support" },
      { label: "Platform Modules", value: "ERP, irrigation engine, satellite monitoring, crop management" },
    ],
    journey: [
      { year: "2024", phase: "Infrastructure & Launch", detail: "Farmtopia engaged with a commercial farm in Minya to deliver both infrastructure and intelligence. A 200 kW solar station was installed, water wells were drilled, and irrigation infrastructure was deployed \u2014 followed by baseline assessment, spatial definition, and agronomic planning to establish the operational foundation." },
      { year: "2025", phase: "Active Operations", detail: "Irrigation management, crop scheduling, and data-driven decision support being delivered as an ongoing partnership. Platform insights guide daily operations alongside the partner's on-ground teams." },
    ],
    metrics: [
      { label: "Farm Area", value: "150 Acres" },
      { label: "Solar", value: "200 kW" },
      { label: "Service Model", value: "ADH Partnership" },
      { label: "Region", value: "West Minya" },
      { label: "Status", value: "Active" },
    ],
    stats: {
      wells: 0,
      irrigationKm: 0,
      ndviGain: "N/A",
      yieldTons: 0,
      waterSaved: "N/A",
      wue: "N/A",
    },
    satelliteTimeseriesRequest: {
      area: [
        [30.761, 28.086],
        [30.766, 28.086],
        [30.766, 28.091],
        [30.761, 28.091],
      ],
      startDate: "2024-01-01",
      endDate: "2025-02-28",
      interval: "quarterly",
      maxScenes: 6,
      maxCloudCover: 70,
      layer: "NDVI",
      layers: ["NDVI", "NATURAL_COLOR", "MOISTURE"],
      label: "Minya Partner Farm",
    },
    publicPreviewId: null,
    technologyIndices: [0, 2, 6, 7],
    waterSource: "Client infrastructure",
    fieldsCount: 0,
    preview: null,
  },
};

const TECHNICAL_OVERRIDE_KEYS = [
  // "crops" is intentionally NOT overridden — it is language-specific content,
  // so it must come from the per-language JSON (en.json/ar.json) and render in
  // Arabic in AR mode. The named crop form (e.g. "Egyptian Cotton: Giza …")
  // now lives directly in the JSON. (audit V2-1 / V2-2)
  "challengeBg",
  "publicPreviewId",
  "satelliteTimeseriesRequest",
  "technologyIndices",
  "waterSource",
  "fieldsCount",
  "preview",
  "lightCase",
];

const pickTechnicalOverrides = (farmId) => {
  const override = ENTERPRISE_CASE_STUDY_OVERRIDES[farmId];

  if (!override) {
    return {};
  }

  return TECHNICAL_OVERRIDE_KEYS.reduce((acc, key) => {
    if (key in override) {
      acc[key] = override[key];
    }

    return acc;
  }, {});
};

// IDs temporarily hidden from the public listing
const HIDDEN_CASE_STUDIES = new Set(["minya"]);

export const mergeEnterpriseCaseStudyFarms = (farms = []) =>
  farms
    .filter((farm) => !HIDDEN_CASE_STUDIES.has(farm.id))
    .map((farm) => ({
      ...farm,
      ...pickTechnicalOverrides(farm.id),
    }));
